/**
 * Module-level refs for the style panel "unsaved changes" flow.
 * StylesEditStyle sets these when mounted and clears them on unmount.
 * Any code that can switch away from the style panel (Sidebar, OptionPlaceholder, etc.)
 * should call setPendingStylePanelAction() before showing the toast, then getShouldBlockStylePanelClose() before dispatching setActivePanel.
 */

export const STYLE_PANEL_UNSAVED_TOAST_TYPE = 'style-panel-unsaved';

export const stylePanelBeforeCloseRef: {
  current: (() => boolean) | null;
} = { current: null };

/** Panel to switch to after Save or Discard (e.g. '', 'Styles', 'Elements'). Set before showing the unsaved toast. */
export const pendingStylePanelActionRef: {
  current: string;
} = { current: '' };

export function setPendingStylePanelAction(panel: string): void {
  pendingStylePanelActionRef.current = panel;
}

/** Called when user clicks "Save Style" in the toast. Run save then navigate. ToastContainer dismisses after. */
export const stylePanelSaveAndCloseRef: {
  current: (() => Promise<void>) | null;
} = { current: null };

/** Called when user clicks "Leave without saving". Navigate only. ToastContainer dismisses after. */
export const stylePanelDiscardAndCloseRef: {
  current: (() => void) | null;
} = { current: null };

export const STYLE_PANEL_UNSAVED_TOAST = {
  text: 'You have unsaved style changes.',
  state: 'warning' as const,
  toastType: STYLE_PANEL_UNSAVED_TOAST_TYPE,
  buttonPrimaryText: 'Save Style',
  buttonSecondaryText: 'Leave without saving',
  showCloseButton: true,
  autoDismiss: false,
};

/**
 * Returns true if the style panel is open and has unsaved changes (should block navigation).
 * Consume before changing panels, e.g. in Sidebar handleIconClick or before setActivePanel in OptionPlaceholder.
 */
export function getShouldBlockStylePanelClose(): boolean {
  return stylePanelBeforeCloseRef.current?.() ?? false;
}
